import { useQuery } from '@apollo/client';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Switch,
  Typography
} from '@mui/material';

import { useFormikContext } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import QUERY_PREDEFINED_PRIO from 'gql/queries/ModulesService/getPredefinedPrioritization.gql';

import GQLGuard from 'app/components/GQLGuard';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

import { SemesterEnum } from '../ParticipantConfiguration';
import { CustomPrioritization } from './CustomPrioritization';

interface MoreDetailsProps {
  index: number;
  participantConfig: any;
}

const showPriorities = prio => {
  return (
    <div>
      {
        prio.reduce((prevSemester, newSemester) => ({
          semester: prevSemester.semester + ' - ' + newSemester.semester
        })).semester
      }
    </div>
  );
};

enum SemesterEnumBachelor {
  B1 = 'B1',
  B2 = 'B2',
  B3 = 'B3',
  B4 = 'B4',
  B5 = 'B5',
  B6 = 'B6',
  B7 = 'B7',
  B8 = 'B8',
  B9 = 'B9'
}

enum SemesterEnumMaster {
  M1 = 'M1',
  M2 = 'M2',
  M3 = 'M3'
}

export function MoreDetails({ index, participantConfig }: MoreDetailsProps) {
  const { values, setFieldValue, touched, errors } = useFormikContext<any>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [allAdmissionsChecked, setAllAdmissionsChecked] = useState(false);
  const predefinedPrioritizationQuery = useQuery<any, any>(QUERY_PREDEFINED_PRIO, {
    context: { clientName: 'federation' }
  });

  const handleChangeAdmissionCriteria = (admission: string) => {
    if (admission) {
      const alreadyInArray =
        values.participantsConfigurations[index].admissionCriteria.indexOf(admission);
      let admissionCriteria: string[] = [
        ...values.participantsConfigurations[index].admissionCriteria
      ];
      alreadyInArray === -1
        ? admissionCriteria.push(admission)
        : (admissionCriteria = admissionCriteria.filter(item => {
            return item !== admission;
          }));
      return setFieldValue(
        `participantsConfigurations[${index}].admissionCriteria`,
        admissionCriteria
      );
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item>
        <FormControl
          className="moduleModal__body__input"
          required={!!values.id && !values.isDefault}
          fullWidth
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <FormLabel focused={false} className="moduleModal__body__input__label">
                {t(
                  translations.pages.modules.create.prioritizationAndApproval.participantConfiguration.admissionCriteria()
                )}
              </FormLabel>
            </Grid>
            <Grid item>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={allAdmissionsChecked}
                    onChange={() => {
                      setAllAdmissionsChecked(old => {
                        old
                          ? setFieldValue(
                              `participantsConfigurations[${index}].admissionCriteria`,
                              []
                            )
                          : setFieldValue(
                              `participantsConfigurations[${index}].admissionCriteria`,
                              Object.keys(SemesterEnum)
                            );
                        return !old;
                      });
                    }}
                  />
                }
                label={t(translations.pages.modules.create.prioritizationAndApproval.selectAll())}
              />
            </Grid>
          </Grid>
          <Grid
            container
            item
            xs={12}
            justifyContent="flex-start"
            columnSpacing={2}
            alignItems="center"
          >
            <Grid item>
              <FormLabel focused={false} className="moduleModal__body__input__helper">
                {t(
                  translations.pages.modules.create.prioritizationAndApproval.participantConfiguration.bachelor()
                )}
              </FormLabel>
            </Grid>
            <Grid item xs={12}>
              <FormGroup row>
                {Object.keys(SemesterEnumBachelor).map(semester => {
                  return (
                    <FormControlLabel
                      value={semester}
                      key={semester}
                      control={
                        <Checkbox
                          color="primary"
                          checked={
                            participantConfig.admissionCriteria.indexOf(semester) === -1
                              ? false
                              : true
                          }
                          onChange={() => {
                            handleChangeAdmissionCriteria(semester);
                          }}
                        />
                      }
                      label={semester}
                    />
                  );
                })}
              </FormGroup>
            </Grid>
          </Grid>
          <Grid container item xs={12}>
            <Grid item>
              <FormLabel focused={false} className="moduleModal__body__input__helper">
                {t(
                  translations.pages.modules.create.prioritizationAndApproval.participantConfiguration.master()
                )}
              </FormLabel>
            </Grid>
            <Grid item xs={12}>
              <FormGroup row>
                {Object.keys(SemesterEnumMaster).map(semester => {
                  return (
                    <FormControlLabel
                      value={semester}
                      key={semester}
                      control={
                        <Checkbox
                          color="primary"
                          checked={
                            participantConfig.admissionCriteria.indexOf(semester) === -1
                              ? false
                              : true
                          }
                          onChange={() => {
                            handleChangeAdmissionCriteria(semester);
                          }}
                        />
                      }
                      label={semester}
                    />
                  );
                })}
              </FormGroup>
            </Grid>
          </Grid>
          <Grid container item xs={12}>
            <FormLabel focused={false} className="moduleModal__body__input__label">
              {t(
                translations.pages.modules.create.prioritizationAndApproval.participantConfiguration.prioritization()
              )}
            </FormLabel>
            <Grid container>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    name={`participantsConfigurations[${index}].custom`}
                    checked={values.participantsConfigurations[index].custom}
                    onChange={e => {
                      setFieldValue(`participantsConfigurations[${index}].prioritization`, []);
                      setFieldValue(
                        `participantsConfigurations[${index}].custom`,
                        e.target.checked
                      );
                    }}
                  />
                }
                label={t(
                  translations.pages.modules.create.prioritizationAndApproval.participantConfiguration.customPrioritization()
                )}
              />
            </Grid>
            {!!values.participantsConfigurations[index].custom ? (
              <CustomPrioritization index={index} />
            ) : (
              <GQLGuard
                query={predefinedPrioritizationQuery}
                onError={error => {
                  dispatch(
                    AlertActions.error(
                      `${t(translations.pages.modules.alerts.failedLoadingPrioritization())}`
                    )
                  );
                  navigate('/', { replace: true });
                  return null;
                }}
              >
                {data => {
                  return (
                    <FormControl fullWidth variant="outlined">
                      <Select
                        autoWidth
                        SelectDisplayProps={{
                          style: {
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                            minHeight: 33,
                            alignContent: 'center'
                          }
                        }}
                        displayEmpty
                        value={
                          values.participantsConfigurations?.[index].prioritization.length > 0
                            ? values.participantsConfigurations?.[index]?.prioritization
                            : ''
                        }
                        onChange={e => {
                          setFieldValue(
                            `participantsConfigurations[${index}].prioritization`,
                            e.target.value
                          );
                        }}
                        fullWidth
                        error={
                          (touched.participantsConfigurations as unknown as any)?.prioritization &&
                          (errors.participantsConfigurations as unknown as any)?.prioritization
                        }
                        required={!!values.id && !values.isDefault}
                        renderValue={(selected: any) => {
                          if (!selected || selected.length === 0) {
                            return (
                              <Typography style={{ color: 'grey' }}>
                                {t(
                                  translations.pages.modules.create.prioritizationAndApproval.participantConfiguration.prioritizationEmpty()
                                )}
                              </Typography>
                            );
                          }
                          return selected.map(s => s.semester).join(' - ');
                        }}
                      >
                        {data.getPredefinedPrioritization.map(priority => {
                          const prio = priority.prioritization
                            .slice()
                            .sort(
                              (prevPrio, currentPrio) => prevPrio.priority - currentPrio.priority
                            );
                          return (
                            <MenuItem key={priority.id} value={priority.prioritization}>
                              {showPriorities(prio)}
                            </MenuItem>
                          );
                        })}
                      </Select>
                      <FormHelperText>
                        {(touched.participantsConfigurations as unknown as any)?.prioritization &&
                          (errors.participantsConfigurations as unknown as any)?.prioritization}
                      </FormHelperText>
                    </FormControl>
                  );
                }}
              </GQLGuard>
            )}
          </Grid>
        </FormControl>
      </Grid>
    </Grid>
  );
}
